import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { listCompetitions, getLoyaltyStats, getMyEntries, type Competition, type LoyaltyStats, type EntryResponse } from '../services/api';
import { formatEntryFee, getCountdown } from '../utils/format';
import { PotentialEarningsPanel } from '../components/PotentialEarningsPanel';
import { SkillWinnerShowcase } from '../components/SkillWinnerShowcase';
import CompetitionVideoSurface from '../components/CompetitionVideoSurface';
import { QrCode } from '../components/QrCode';
import { MoreFromMyInvestments } from '../components/MoreFromMyInvestments';
import { WelcomeNudge } from '../components/WelcomeNudge';
import { SiteHeader } from '../components/SiteHeader';
import { SocialBar } from '../components/SiteFooter';
import { useAuth } from '../contexts/AuthContext';

/** Sister product — cross-link in the top nav. */
const LOTTO_URL = 'https://lotto.my-investments.io';
/** Walkthrough advert (How-it-works embed) — GCS-hosted, public.
 *  ?v=3 cache-busts the v3 re-cut (hotfix-1-163) — recorded against the live
 *  "Win by Skill. Not by Luck." hero with the free share-to-enter route — past
 *  the 1h GCS/CDN cache so viewers get the new render immediately. */
const WALKTHROUGH_VIDEO =
  'https://storage.googleapis.com/prizes-flyers-prod/marketing/walkthrough/prizes-walkthrough-landscape.mp4?v=3';
const WALKTHROUGH_POSTER =
  'https://storage.googleapis.com/prizes-flyers-prod/marketing/walkthrough/poster.jpg';
import { VERSION } from '../version';

/**
 * Parent / operating company — facts sourced from marome-investments.co.za.
 * These power the "Why you can trust us" band and the footer legal line.
 * COMPANY_REG_NO stays blank until the operator supplies the CIPC registration
 * number; once set it renders automatically. We never show a fabricated number.
 */
const COMPANY_REG_NO = '2021/853258/07'; // CIPC registration number
const COMPANY_ADDRESS = 'Sandton, Johannesburg, South Africa, 2191';
const SUPPORT_EMAIL = 'info@marome-investments.co.za';
const SUPPORT_WHATSAPP_URL = 'https://wa.me/27791451911';

// Exported so tests can assert which statuses are included in the featured feed
export const FEATURED_STATUSES = new Set(['open', 'countdown_active']);

// Reframed to carry context + the collective model (not a bare "win a prize").
const LANDING_SHARE_URL = 'https://prizes.my-investments.io';

// hotfix-1-159: the URL the landing-page QR code encodes. Prefer the live origin
// so it is environment-correct (localhost in dev, the real host in PROD) with no
// hardcoding; fall back to the canonical public URL for non-browser/test
// contexts where window is unavailable.
export function getSiteUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return LANDING_SHARE_URL;
}

// hotfix-1-54: when a logged-in user shares, append their referral code so the
// recipient lands with the sender's code attached. The app already reads
// ?ref=<code> on any page (App.tsx → sessionStorage 'pending_referral_code' →
// RegisterPage prefills used_referral_code). Guests share the bare URL unchanged.
export function buildLandingWhatsappUrl(referralCode?: string | null): string {
  const url = referralCode
    ? `${LANDING_SHARE_URL}/?ref=${encodeURIComponent(referralCode)}`
    : LANDING_SHARE_URL;
  return `https://wa.me/?text=${encodeURIComponent(
    `🏆 Win real prizes in a skill competition — entries from R150. Play the challenge, best skill wins. Together we can afford it 👉 ${url}`,
  )}`;
}

type FeedStatus = 'loading' | 'success' | 'error';

/** Skeleton card shown while competitions are loading — not visible to screen readers */
function SkeletonCard() {
  return (
    <li aria-hidden="true" className="w-full overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
      <div className="h-40 animate-pulse bg-gray-200" />
      <div className="space-y-3 p-4">
        <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200" />
      </div>
    </li>
  );
}

interface FeaturedCardProps {
  competition: Competition;
  /** Pre-formatted entry fee label (e.g. "Enter for R50") */
  feeLabel: string;
  /** Pre-formatted countdown label (e.g. "2d 4h remaining") or null */
  countdownLabel: string | null;
}

/** Mini competition card for the featured feed */
function FeaturedCard({ competition, feeLabel, countdownLabel }: FeaturedCardProps) {
  // prize_media_urls is string[] (non-optional) — use [0] directly
  const heroUrl = competition.prize_media_urls[0] ?? null;

  return (
    <li>
      <Link
        to={`/competitions/${competition.id}`}
        className="block w-full overflow-hidden rounded-xl border border-gray-100 bg-white transition hover:border-violet-200 hover:shadow-md active:scale-[0.98]"
      >
        <div className="relative aspect-video w-full overflow-hidden">
          <CompetitionVideoSurface
            title={competition.title}
            videoAsset={competition.video_asset}
            fallbackImageUrl={heroUrl}
            className="h-full w-full"
            showBadge={true}
          />
        </div>
        <div className="space-y-2 p-4">
          <h3 className="line-clamp-2 text-sm font-bold text-violet-900">
            {competition.title}
          </h3>
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-bold text-white"
            style={{
              background: 'linear-gradient(90deg, #7c3aed 0%, #ec4899 100%)',
            }}
          >
            {feeLabel}
          </span>
          {countdownLabel && (
            <p className="text-xs text-gray-500">{countdownLabel}</p>
          )}
        </div>
      </Link>
    </li>
  );
}


export default function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // hotfix-1-54: carry the logged-in user's referral code in the share link.
  const whatsappShareUrl = buildLandingWhatsappUrl(user?.referral_code);
  const [feedStatus, setFeedStatus] = useState<FeedStatus>('loading');
  const [competitions, setCompetitions] = useState<Competition[]>([]);

  // Loyalty stats — drives the PotentialEarningsPanel aspiration bar (the public
  // Live Stats ticker was removed in hotfix-1-44; those counts are admin-only).
  const [loyaltyStats, setLoyaltyStats] = useState<LoyaltyStats | null>(null);

  const fetchCompetitions = useCallback(() => {
    setFeedStatus('loading');
    listCompetitions()
      .then((res) => {
        // Show ALL open competitions — this page is now the full browse experience.
        const open = res.data.filter((c) => FEATURED_STATUSES.has(c.status));
        setCompetitions(open);
        setFeedStatus('success');
      })
      .catch(() => {
        setFeedStatus('error');
      });
  }, []);

  useEffect(() => {
    fetchCompetitions();
  }, [fetchCompetitions]);

  useEffect(() => {
    getLoyaltyStats()
      .then((r) => setLoyaltyStats(r.data))
      .catch((err) => {
        // Non-fatal — the earnings panel gracefully degrades without stats.
        console.error('[LandingPage] failed to load loyalty stats:', err);
      });
  }, []);

  // v4-4-12: entries that still need a skill answer (skill added after the user entered).
  const [needsSkill, setNeedsSkill] = useState<EntryResponse[]>([]);
  const [needsSkillDismissed, setNeedsSkillDismissed] = useState(false);
  useEffect(() => {
    if (!user) {
      setNeedsSkill([]);
      return;
    }
    getMyEntries()
      .then((r) =>
        setNeedsSkill(
          r.data.filter(
            (e) =>
              e.status === 'confirmed' &&
              !!e.skill_type &&
              e.skill_type !== 'none' &&
              e.has_skill_answer === false,
          ),
        ),
      )
      .catch(() => setNeedsSkill([]));
  }, [user]);

  // First-time-visitor welcome nudge — scroll straight to the competitions list,
  // the one obvious next action toward a first entry. Smooth-scroll unless the
  // visitor prefers reduced motion. Only shown to logged-out (i.e. likely new)
  // visitors; the WelcomeNudge itself also gates on a localStorage "seen" flag.
  const scrollToCompetitions = useCallback(() => {
    const el = document.getElementById('competitions');
    if (!el) return;
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {!user && <WelcomeNudge onPrimaryAction={scrollToCompetitions} />}
      {user && needsSkill.length > 0 && !needsSkillDismissed && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-3 text-sm font-semibold text-white sm:px-6"
        >
          <span>
            You have {needsSkill.length}{' '}
            {needsSkill.length === 1 ? 'entry' : 'entries'} that need a skill answer —
            complete the challenge to validate{' '}
            {needsSkill.length === 1 ? 'it' : 'them'}.{' '}
            <Link
              to={`/competitions/${needsSkill[0].competition_id}`}
              className="underline underline-offset-2"
            >
              Complete now →
            </Link>
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setNeedsSkillDismissed(true)}
            className="cursor-pointer shrink-0 rounded-full px-2 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}
      {/* Shared customer header (hotfix-1-169) — persistent "How it works" + auth controls */}
      <SiteHeader />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)',
        }}
      >
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
          <div className="text-center">
            <h1
              className="font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {t('landing.hero_headline')}
            </h1>
            <p
              className="mx-auto mt-6 max-w-2xl text-lg text-white/90 sm:text-xl"
            >
              {t('landing.hero_subheadline')}
            </p>
            <p className="mx-auto mt-4 max-w-xl text-base font-medium italic text-white/75 sm:text-lg">
              {t('landing.hero_collective_slogan')}
            </p>
            <p className="mx-auto mt-6 inline-flex items-center rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 sm:text-base">
              {t('landing.hero_value_hook')}
            </p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a
                href="#competitions"
                className="inline-flex min-h-[48px] items-center justify-center rounded-button bg-white px-8 py-3 text-base font-bold text-violet-700 shadow-lg transition hover:bg-violet-50 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-white/50"
              >
                {t('landing.cta_browse')}
              </a>
              <Link
                to="/register"
                className="inline-flex min-h-[48px] items-center justify-center rounded-button border-2 border-white/80 bg-transparent px-8 py-3 text-base font-bold text-white transition hover:bg-white/10 focus:outline-none focus:ring-4 focus:ring-white/50"
              >
                {t('landing.cta_register')}
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative wave — aria-hidden so screen readers skip the entire decoration */}
        <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
          <svg
            viewBox="0 0 1440 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
            preserveAspectRatio="none"
          >
            <path
              d="M0 80L1440 80L1440 20C1200 80 960 0 720 40C480 80 240 0 0 20L0 80Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* mktg-student-village: promotional tile → the Student Village landing.
          Sits directly under the hero so it's the first thing visitors see
          (moved up from below the competitions list). */}
      <section className="mx-auto max-w-6xl px-4 pt-8 pb-2 sm:px-6 lg:px-8">
        <Link
          to="/student-village"
          className="group flex cursor-pointer flex-col items-start gap-4 rounded-card border border-violet-100 bg-violet-50/60 p-6 transition-colors duration-200 hover:bg-violet-50 sm:flex-row sm:items-center sm:justify-between sm:p-8"
        >
          <div>
            <span className="inline-block rounded-pill bg-gradient-to-r from-violet-600 to-pink-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
              For students
            </span>
            <h3 className="mt-3 font-heading text-xl font-bold text-text-primary sm:text-2xl">
              Student Village — win res, tuition, groceries &amp; more
            </h3>
            <p className="mt-1 max-w-xl text-sm text-text-secondary sm:text-base">
              Skill-based competitions that fund what students actually need. Enter from R70, or free by sharing.
            </p>
          </div>
          <span className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-button bg-violet-700 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 group-hover:bg-violet-800">
            Explore Student Village
            <span aria-hidden="true">→</span>
          </span>
        </Link>
      </section>

      {/* Skill winner showcase — a "closest answer wins" number-line visual that
          shows skill (the closest valid answer) decides the winner, not luck.
          Replaced the old spinning-names raffle globe (hotfix-1-156). Sits
          between the hero and How-it-works. */}
      <SkillWinnerShowcase />

      {/* How it works — plain 3-step explainer for first-time visitors.
          Deliberately omits the hidden entry-threshold mechanics. */}
      <section id="how-it-works" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 scroll-mt-20">
        <div className="mb-10 text-center">
          <h2 className="font-heading text-2xl font-bold text-violet-900 sm:text-3xl">
            {t('landing.howitworks_title')}
          </h2>
          <p className="mt-2 text-base text-gray-500">{t('landing.howitworks_subtitle')}</p>
        </div>
        <ol className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((step) => (
            <li
              key={step}
              className="relative rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
            >
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}
                aria-hidden="true"
              >
                {step}
              </span>
              <h3 className="mt-4 text-lg font-bold text-violet-900">
                {t(`landing.howitworks_step${step}_title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {t(`landing.howitworks_step${step}_body`)}
              </p>
            </li>
          ))}
        </ol>

        {/* Walkthrough video — click-to-play with poster (no 9MB autoload).
            Hosted on GCS; landscape 16:9 master. */}
        <div className="mx-auto mt-12 max-w-3xl">
          <p className="mb-3 text-center text-sm font-medium text-gray-500">
            {t('landing.howitworks_video_caption')}
          </p>
          <div className="overflow-hidden rounded-2xl border border-violet-100 shadow-card">
            <video
              className="block aspect-video w-full bg-black"
              controls
              preload="none"
              playsInline
              poster={WALKTHROUGH_POSTER}
              aria-label={t('landing.howitworks_video_aria')}
            >
              <source src={WALKTHROUGH_VIDEO} type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Competitions — full browse list (merged from the old /competitions page) */}
      <section id="competitions" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 scroll-mt-20">
        <div className="mb-8">
          <h2
            className="font-heading text-2xl font-bold text-violet-900 sm:text-3xl"
          >
            {t('landing.featured_title')}
          </h2>
        </div>

        {/* Accessible live region: announces loading/error state to screen readers */}
        <p role="status" aria-live="polite" className="sr-only">
          {feedStatus === 'loading' ? t('landing.loading_competitions') : ''}
        </p>

        {feedStatus === 'error' ? (
          <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center">
            <p className="mb-3 text-sm text-red-600">{t('landing.competitions_error')}</p>
            <button
              type="button"
              onClick={fetchCompetitions}
              className="rounded-button px-4 py-2 text-sm font-semibold text-white transition"
              style={{ background: 'linear-gradient(90deg, #7c3aed 0%, #ec4899 100%)' }}
            >
              {t('landing.try_again')}
            </button>
          </div>
        ) : feedStatus === 'loading' ? (
          <ul
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            role="list"
            data-testid="skeleton-list"
          >
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </ul>
        ) : competitions.length === 0 ? (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-10 text-center text-sm text-gray-400">
            {t('landing.featured_none')}
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" role="list">
            {competitions.map((c) => {
              const countdown = getCountdown(c.draw_date);
              const countdownLabel = countdown
                ? t('landing.countdown_remaining', {
                    days: countdown.days,
                    hours: countdown.hours,
                  })
                : null;
              return (
                <FeaturedCard
                  key={c.id}
                  competition={c}
                  feeLabel={t('landing.entry_fee', {
                    fee: formatEntryFee(c.entry_fee_cents, c.currency_code),
                  })}
                  countdownLabel={countdownLabel}
                />
              );
            })}
          </ul>
        )}
      </section>

      {/* Why you can trust us — trust band for first-time, never-heard-of-us
          visitors. Concrete, verifiable claims only. */}
      <section id="trust" className="border-y border-gray-100 bg-violet-50/40 py-16 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-center font-heading text-2xl font-bold text-violet-900 sm:text-3xl">
            {t('landing.trust_title')}
          </h2>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
            {[
              { key: 'company', icon: '🏢' },
              { key: 'payments', icon: '🔒' },
              { key: 'winners', icon: '🏆' },
              { key: 'notgambling', icon: '🤝' },
              { key: 'support', icon: '💬' },
            ].map(({ key, icon }) => (
              <li
                key={key}
                className="flex gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <span className="text-2xl" aria-hidden="true">
                  {icon}
                </span>
                <div>
                  <h3 className="text-sm font-bold text-violet-900">
                    {t(`landing.trust_${key}_label`)}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    {t(`landing.trust_${key}_body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Potential Earnings Section.
          The public "Live Stats" ticker (Members / Referrals / Rewards) was removed
          (hotfix-1-44) — those community counts are admin-only (see /admin/loyalty).
          The aspiration panel stays: its headline is a fixed max ("Earn up to RX"),
          not a live community count. */}
      <section className="bg-surface-2 py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <PotentialEarningsPanel variant="landing" stats={loyaltyStats} />
        </div>
      </section>

      {/* Share Section */}
      <section className="py-12 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2
            className="font-heading text-2xl font-bold text-[#7c3aed] mb-2"
          >
            Share with friends, earn rewards
          </h2>
          <p
            className="text-gray-600 mb-6"
          >
            Refer a friend and earn rewards when they enter their first competition.
          </p>
          <a
            href={whatsappShareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-6 py-3 rounded-xl transition-colors min-h-[44px]"
            aria-label="Share on WhatsApp"
          >
            {/* WhatsApp icon */}
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Share on WhatsApp
          </a>
        </div>
      </section>

      {/* More from My Investments — attorney-approved affiliate cards (hotfix-1-161).
          Sibling-product cross-promo with LIVE "from" prices from the payments
          service. Sits after the share band and before the QR block; decorative,
          degrades gracefully (hides itself on load failure). */}
      <MoreFromMyInvestments />

      {/* Open on your phone — scan-to-open QR (hotfix-1-159). Brand-consistent
          gradient-bordered card; placed just above the footer so it is handy
          without crowding the hero. The QR encodes the live site origin so it
          works correctly in every environment. */}
      <section className="bg-white py-12" data-testid="open-on-phone">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl p-[2px]"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)' }}
          >
            <div className="flex flex-col items-center gap-6 rounded-2xl bg-white p-6 text-center sm:flex-row sm:items-center sm:gap-8 sm:p-8 sm:text-left">
              <div className="shrink-0 rounded-xl border border-violet-100 bg-white p-3 shadow-sm">
                <QrCode
                  value={getSiteUrl()}
                  size={144}
                  label="QR code to open the site on your phone"
                />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-violet-900 sm:text-2xl">
                  Open on your phone
                </h2>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-600">
                  Scan this code with your phone camera to open the site on your mobile
                  and keep playing on the go.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-white py-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SocialBar className="mb-6 border-b border-gray-100 pb-6" />
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <p className="text-sm text-text-muted">
              {t('landing.footer_copyright', { year: new Date().getFullYear() })}
              <span className="ml-2 text-xs text-text-muted/70">v{VERSION}</span>
            </p>
            <nav aria-label="Footer navigation" className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/how-it-works" className="text-sm text-text-muted hover:text-violet-700 transition">
                {t('landing.howitworks_title')}
              </Link>
              <a href="#competitions" className="text-sm text-text-muted hover:text-violet-700 transition">
                {t('landing.footer_browse')}
              </a>
              <Link to="/winners" className="text-sm text-text-muted hover:text-violet-700 transition">
                {t('landing.footer_winners')}
              </Link>
              <Link to="/register" className="text-sm text-text-muted hover:text-violet-700 transition">
                {t('landing.footer_register')}
              </Link>
              {/* corp-1-5: corporate "Partner with us / Sponsor a prize" link-out */}
              <Link to="/partners" className="text-sm text-text-muted hover:text-violet-700 transition">
                {t('landing.footer_partners')}
              </Link>
              <a href={LOTTO_URL} className="text-sm font-semibold text-violet-600 hover:text-violet-800 transition">
                Play the Lotto ↗
              </a>
            </nav>
          </div>

          {/* Legal / operating-company line — registered SA company + contact */}
          <div className="mt-6 border-t border-gray-100 pt-6 text-center text-xs leading-relaxed text-text-muted/80">
            <p>
              {t('landing.footer_operated_by')}
              {COMPANY_REG_NO && (
                <>
                  {' '}
                  {t('landing.footer_reg_no', { regNo: COMPANY_REG_NO })}
                </>
              )}
            </p>
            <p className="mt-1">{COMPANY_ADDRESS}</p>
            <p className="mt-1">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-violet-700 transition">
                {SUPPORT_EMAIL}
              </a>
              <span className="mx-2" aria-hidden="true">·</span>
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-violet-700 transition"
              >
                {t('landing.footer_whatsapp')}
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
